import { createContext, useContext, useState, useEffect } from 'react'
import axios from 'axios'

const AuthContext = createContext(null)

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api'

// Set axios header immediately if token exists in localStorage
const initialToken = localStorage.getItem('token')
if (initialToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${initialToken}`
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(initialToken)
  const [loading, setLoading] = useState(true)

  // Set up axios defaults when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
      localStorage.setItem('token', token)
    } else {
      delete axios.defaults.headers.common['Authorization']
      localStorage.removeItem('token')
    }
  }, [token])

  // Check auth status on mount
  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('token')
      if (!storedToken) {
        setLoading(false)
        return
      }

      // Ensure header is set before making request
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`

      try {
        const response = await axios.get(`${API_URL}/auth/me`)
        setUser(response.data)
        setToken(storedToken)
      } catch (error) {
        console.error('Auth check failed:', error)
        setToken(null)
        setUser(null)
        localStorage.removeItem('token')
      } finally {
        setLoading(false)
      }
    }

    checkAuth()
  }, [])

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password })
    setToken(response.data.access_token)
    setUser(response.data.user)
    return response.data
  }

  const register = async (email, password, name, role = 'driver', inviteCode = null) => {
    const payload = { email, password, name, role }
    if (inviteCode) {
      payload.invite_code = inviteCode
    }
    const response = await axios.post(`${API_URL}/auth/register`, payload)
    setToken(response.data.access_token)
    setUser(response.data.user)
    return response.data
  }

  const logout = () => {
    setToken(null)
    setUser(null)
  }

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    isOwner: user?.is_owner ?? false
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
